// Brazilian registry documents as Effect Schema. Each decodes to the same
// branded type as its `@finnest/core/schemas/br` Zod schema, so a value flows
// unchanged between Effect owners and the code still on Zod.
//
// The CNPJ is alphanumeric from July 2026 (IN RFB 2.229/2024; Serpro/Receita Federal, "Cálculo
// dos dígitos verificadores do CNPJ alfanumérico"): twelve characters of 0-9 or A-Z, then two
// numeric check digits. Each character is worth its ASCII code minus 48, so a digit keeps its
// value and A-Z are worth 17-42; the weights 2..9 from the right and the modulus 11 are those of
// the numeric CNPJ, which is the all-digit subset. The Effect `Cnpj` is therefore a superset of
// the frozen Zod `Cnpj`, which stays numeric under the same brand. This module is the only place
// the rule lives: every other CNPJ check digit and every hand-written CNPJ pattern in the platform
// imports it from here.
//
// Documented exception (FNSTPWR-351, CTO decision): wire schemas that mirror the OPIN swagger keep
// its spelling `^[A-Z0-9]{12}\d{2}$`, because the OPIN schema-contract check
// (packages/insurance/customer-holder/scripts/check-schema-contract.py) compares the pattern text
// with the pinned swagger verbatim and the OPIN and OFB swaggers spell the same set differently.
// They accept exactly the strings `CNPJ_PATTERN` accepts:
// - insurance/customer-holder: identity/documents.ts, identity/procurators.ts,
//   records/personalidentificationdata.ts, records/businessidentificationdata.ts
// - insurance/consent-holder: consents/schema/atoms/consent.ts (`HolderBusinessEntity`)
// - insurance/open-data: data-channels/schema.ts (`cnpjNumber`)
// - insurance/product-data-holder: domain/primitives.ts (`CnpjNumber`)
// The generated `*.gen.ts` quote schemas of insurance/service-initiation-holder mirror it as well.
import { Array as Arr, Schema } from 'effect';
import type { Cnpj as CnpjType } from '../../../schemas/br/core/cnpj.ts';

/**
 * Canonical CNPJ: upper case, no mask; twelve characters of 0-9 or A-Z, then two digits. Spelled
 * as the OFB swaggers publish it, so a wire schema built on it keeps its published OpenAPI pattern.
 */
export const CNPJ_PATTERN = /^[0-9A-Z]{12}[0-9]{2}$/u;

const CNPJ_BASE_LENGTH = 12;
const CHECK_MODULUS = 11;
/** A remainder below this yields a check digit of 0 rather than `modulus - rest`. */
const CHECK_REMAINDER_FLOOR = 2;
const LOWEST_WEIGHT = 2;
/** Weights run 2..9 from the rightmost source character leftwards, then repeat. */
const WEIGHT_CYCLE_LENGTH = 8;
/** A character is worth its ASCII code minus that of `0`. */
const CHARACTER_VALUE_ORIGIN = 48;

const checkDigit = (source: string): number => {
  const sum = Arr.makeBy(
    source.length,
    (index) =>
      (source.charCodeAt(index) - CHARACTER_VALUE_ORIGIN) *
      (((source.length - 1 - index) % WEIGHT_CYCLE_LENGTH) + LOWEST_WEIGHT),
  ).reduce((total, weighted) => total + weighted, 0);
  const rest = sum % CHECK_MODULUS;
  return rest < CHECK_REMAINDER_FLOOR ? 0 : CHECK_MODULUS - rest;
};

/**
 * Whether `document` is a CNPJ in `CNPJ_PATTERN` whose last two digits are the check digits of
 * the characters before them. A CNPJ of one repeated character is never issued, although its
 * digits add up, so it is refused.
 */
export const hasCnpjCheckDigits = (document: string): boolean => {
  if (!CNPJ_PATTERN.test(document) || new Set(document).size === 1) return false;
  const base = document.slice(0, CNPJ_BASE_LENGTH);
  const first = checkDigit(base);
  const second = checkDigit(`${base}${String(first)}`);
  return document.slice(CNPJ_BASE_LENGTH) === `${String(first)}${String(second)}`;
};

/** Cadastro Nacional da Pessoa Jurídica, numeric or alphanumeric, with valid check digits. */
export const Cnpj = Schema.String.check(Schema.isPattern(CNPJ_PATTERN)).pipe(
  Schema.refine((value): value is CnpjType => hasCnpjCheckDigits(value), {
    identifier: 'Cnpj',
    expected: 'a CNPJ of 14 characters with valid check digits',
  }),
);
export type Cnpj = typeof Cnpj.Type;
